/**
 * WorkspaceTransitionStrategy
 *
 * Selects the correct workspace transition path for the Upgrade popup.
 *
 * There are two distinct flows, driven by whether the user already owns a
 * workspace:
 *
 *   - 'create'           First workspace for a brand-new user. Requires a
 *                        workspace name, provisions a NEW Workspace row
 *                        via /api/v1/workspace/upgrade/start, then
 *                        /upgrade/finalize to flip pending_payment →
 *                        active.
 *
 *   - 'upgrade_in_place' Free user who already owns a Workspace row.
 *                        Does NOT insert a new workspace — reuses the
 *                        existing id, flips planType via the
 *                        webhook-authoritative path. Name input is
 *                        skipped (existing workspace already has one).
 *
 * Both strategies expose the same five lifecycle hooks so the popup
 * view can render any plan + any step list without switching on mode
 * internally:
 *
 *   handleStartUpgrade({ planType, billingCycle, workspaceName? })
 *   handleMountStripeElement(container, subscriptionInfo)
 *   handleCompletePayment(stripeElementsInfo)
 *   handleFinalizeUpgrade(workspaceId)
 *   handleCancelUpgrade(workspaceId)
 *
 * The strategy is selected by UpgradeToTeamPopupController based on the
 * `mode` option passed from the caller (defaulting to 'create' for
 * backward compat).
 */

import TrebbleClientAPIHelper from "models/helper/TrebbleClientAPI";
import StripeHelper from "models/helper/StripeHelper";

export const TRANSITION_MODES = {
    CREATE: 'create',
    UPGRADE_IN_PLACE: 'upgrade_in_place',
};

/**
 * @param {'create'|'upgrade_in_place'} mode
 * @returns {Object} strategy with the 5 lifecycle hooks.
 */
export function getWorkspaceTransitionStrategy(mode) {
    if (mode === TRANSITION_MODES.UPGRADE_IN_PLACE) {
        return UpgradeInPlaceStrategy;
    }
    return CreateWorkspaceStrategy;
}

/**
 * Which steps the popup can render for a given mode.
 * Plan type is accepted for API compatibility, but the current popup handles
 * Team extras inside the plan card copy and post-payment invite step. Do not
 * advertise a separate step unless the content renderer implements it.
 */
export function getStepsForTransition(mode, planType) {
    var steps = [];
    // Step: select plan + billing cycle — always shown
    steps.push('plan');
    if (mode !== TRANSITION_MODES.UPGRADE_IN_PLACE) {
        steps.push('name');
    }
    steps.push('payment');
    steps.push('success');
    return steps;
}

export const CreateWorkspaceStrategy = {
    mode: TRANSITION_MODES.CREATE,

    async handleStartUpgrade({ workspaceName, planType, billingCycle }) {
        if (!workspaceName) {
            throw new Error('workspaceName is required for workspace creation');
        }
        var result = await TrebbleClientAPIHelper.getInstance().startWorkspaceUpgrade({
            name: workspaceName,
            planType: planType,
            billingCycle: billingCycle,
        });
        if (!result || !result.clientSecret || !result.workspaceId) {
            throw new Error('startWorkspaceUpgrade did not return a clientSecret');
        }
        return {
            clientSecret: result.clientSecret,
            workspaceId: result.workspaceId,
            mode: result.mode || TRANSITION_MODES.CREATE,
        };
    },

    handleMountStripeElement(container, subscriptionInfo) {
        return StripeHelper.getInstance().showPaymentFormForWorkspaceUpgrade(container, subscriptionInfo);
    },

    async handleCompletePayment(stripeElementsInfo, pendingWorkspaceId) {
        await StripeHelper.getInstance().completeWorkspacePayment(stripeElementsInfo, pendingWorkspaceId);
    },

    async handleFinalizeUpgrade(workspaceId) {
        return TrebbleClientAPIHelper.getInstance().finalizeWorkspaceUpgrade({ workspaceId: workspaceId });
    },

    async handleCancelUpgrade(workspaceId) {
        return TrebbleClientAPIHelper.getInstance().cancelWorkspaceUpgrade({
            workspaceId: workspaceId,
            mode: TRANSITION_MODES.CREATE,
        });
    },
};

export const UpgradeInPlaceStrategy = {
    mode: TRANSITION_MODES.UPGRADE_IN_PLACE,

    async handleStartUpgrade({ planType, billingCycle }) {
        // No workspaceName here — the backend targets the user's existing
        // default personal workspace in-place and ignores `name` if passed.
        var result = await TrebbleClientAPIHelper.getInstance().startWorkspaceUpgrade({
            mode: TRANSITION_MODES.UPGRADE_IN_PLACE,
            planType: planType,
            billingCycle: billingCycle,
        });
        if (!result || !result.clientSecret || !result.workspaceId) {
            throw new Error('startWorkspaceUpgrade did not return a clientSecret');
        }
        return {
            clientSecret: result.clientSecret,
            workspaceId: result.workspaceId,
            mode: result.mode || TRANSITION_MODES.UPGRADE_IN_PLACE,
        };
    },

    handleMountStripeElement(container, subscriptionInfo) {
        return StripeHelper.getInstance().showPaymentFormForWorkspaceUpgrade(container, subscriptionInfo);
    },

    async handleCompletePayment(stripeElementsInfo, pendingWorkspaceId) {
        await StripeHelper.getInstance().completeWorkspacePayment(stripeElementsInfo, pendingWorkspaceId);
    },

    async handleFinalizeUpgrade(workspaceId) {
        return TrebbleClientAPIHelper.getInstance().finalizeWorkspaceUpgrade({ workspaceId: workspaceId });
    },

    async handleCancelUpgrade(workspaceId) {
        // The UI mode is `upgrade_in_place`; the cancel API contract uses
        // `in_place` to release the guard without deleting the workspace row.
        return TrebbleClientAPIHelper.getInstance().cancelWorkspaceUpgrade({
            workspaceId: workspaceId,
            mode: 'in_place',
        });
    },
};

export default {
    TRANSITION_MODES: TRANSITION_MODES,
    getWorkspaceTransitionStrategy: getWorkspaceTransitionStrategy,
    getStepsForTransition: getStepsForTransition,
    CreateWorkspaceStrategy: CreateWorkspaceStrategy,
    UpgradeInPlaceStrategy: UpgradeInPlaceStrategy,
};
